import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { api } from '../utils/api';
import { useData } from '../context/DataContext';
import { resolveStatsStripForDisplay, applyLiveOutletCountToStats } from '../utils/statsStripDisplay';
import './StatsStrip.css';

const PLACEHOLDER_ITEMS = [
  { label: 'Verified Deals', value: '—' },
  { label: 'Trusted Stores', value: '—' },
  { label: 'Avg. User Savings', value: '—' },
  { label: 'System Updates', value: '—' },
];

const StatsStrip = () => {
  const { outlets, initialOutletsLoaded } = useData();
  const [stats, setStats] = useState({ isActive: true, items: [] });
  const [loading, setLoading] = useState(true);
  const [failed, setFailed] = useState(false);

  useEffect(() => {
    let cancelled = false;

    const loadStats = async (attempt = 0) => {
      try {
        const data = await api.get('/content/stats-strip');
        if (cancelled || !data) return;

        let resolved = resolveStatsStripForDisplay(data);
        const liveOutletCount = initialOutletsLoaded
          ? (outlets || []).filter((o) => o.isActive !== false).length
          : 0;
        if (liveOutletCount > 0) {
          resolved = applyLiveOutletCountToStats(resolved, liveOutletCount);
        }
        setStats(resolved);
        setFailed(false);
        setLoading(false);
      } catch {
        if (cancelled) return;
        if (attempt < 1) {
          window.setTimeout(() => loadStats(attempt + 1), 1500);
          return;
        }
        setFailed(true);
        setLoading(false);
      }
    };

    loadStats();
    return () => {
      cancelled = true;
    };
  }, [outlets, initialOutletsLoaded]);

  if (!stats.isActive && !loading && !failed) {
    return null;
  }

  const hasLoadedItems = stats.items.length > 0;
  const hasRealValues = stats.items.some((item) => String(item.value || '').trim() && item.value !== '—');
  const items = loading && !hasLoadedItems
    ? PLACEHOLDER_ITEMS
    : failed && !hasLoadedItems
      ? PLACEHOLDER_ITEMS
      : hasLoadedItems
        ? stats.items
        : PLACEHOLDER_ITEMS;

  const renderBlock = (item, index) => {
    const showPlaceholder = (loading && !hasRealValues) || (failed && !hasRealValues);
    const hasLink = Boolean(item.linkTo);
    const content = (
      <>
        <span className={`stats-strip-number${showPlaceholder ? ' stats-strip-number--loading' : ''}`}>
          {item.value || '—'}
        </span>
        <span className="stats-strip-label">{item.label}</span>
      </>
    );

    if (hasLink && !showPlaceholder) {
      return (
        <Link
          key={`${item.label}-${index}`}
          to={item.linkTo}
          className="stats-strip-block stats-strip-block-link"
        >
          {content}
        </Link>
      );
    }

    return (
      <div key={`${item.label}-${index}`} className="stats-strip-block">
        {content}
      </div>
    );
  };

  return (
    <section
      className={`stats-strip${loading && !hasRealValues ? ' stats-strip--loading' : ''}`}
      aria-label="Platform highlights"
      aria-busy={loading && !hasRealValues}
    >
      <div className="stats-strip-inner">
        {items.map((item, index) => renderBlock(item, index))}
      </div>
    </section>
  );
};

export default StatsStrip;
